/**
 * Navigation Configuration
 * Fuente unica de verdad para la navegacion del sistema.
 * Controla secciones del sidebar, items del AppBar, y permisos RBAC.
 */
import { PERMISSIONS } from "../context/PermissionsContext/PermissionsContext";

// Iconos referenciados por string para importar en componentes
export const SIDEBAR_SECTIONS = [
  {
    id: "clinical",
    labelKey: "nav.clinical",
    labelFallback: "Clinical",
    icon: "LocalHospitalIcon",
    permissions: [
      PERMISSIONS.CAREGAPS_READ,
      PERMISSIONS.CODING_READ,
      PERMISSIONS.AI_USE,
      PERMISSIONS.NOTES_READ,
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.REFERRALS_READ,
      PERMISSIONS.LAB_READ,
      PERMISSIONS.ASSIGNMENTS_WRITE,
    ],
    items: [
      {
        id: "care-gaps",
        path: "/care-gaps",
        labelKey: "nav.careGaps",
        labelFallback: "Care Gaps",
        icon: "BarChartIcon",
        iconColor: "#2196f3",
        permissions: [PERMISSIONS.CAREGAPS_READ],
      },
      {
        id: "coding-opportunities",
        path: "/coding-opportunities",
        labelKey: "nav.codingOpportunities",
        labelFallback: "Coding Opportunities",
        icon: "CodeIcon",
        iconColor: "#ff9800",
        permissions: [PERMISSIONS.CODING_READ],
      },
      {
        id: "ai-clinical-notes",
        path: "/ClinicalNoteAi",
        labelKey: "nav.aiClinicalNotes",
        labelFallback: "AI Clinical Notes",
        icon: "FactCheckIcon",
        iconColor: "#1565c0",
        permissions: [PERMISSIONS.AI_USE],
      },
      // {
      //   id: "clinical-notes",
      //   path: "/ClinicalNotesList",
      //   labelKey: "nav.clinicalNotes",
      //   labelFallback: "Clinical Notes",
      //   icon: "DescriptionIcon",
      //   iconColor: "#43a047",
      //   permissions: [PERMISSIONS.NOTES_READ],
      // },
      {
        id: "medication-reconciliation",
        path: "/MedicationReconciliationPage",
        labelKey: "nav.medicationReconciliation",
        labelFallback: "Medication Reconciliation",
        icon: "MedicationIcon",
        iconColor: "#be07e2",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "specialist-referrals",
        path: "/specialist-referrals",
        labelKey: "nav.specialistReferrals",
        labelFallback: "Specialist Referrals",
        icon: "LocalHospitalIcon",
        iconColor: "#7986cb",
        permissions: [PERMISSIONS.REFERRALS_READ],
      },
      {
        id: "lab-phlebotomy",
        path: "/LabPhlebotomy",
        labelKey: "nav.labPhlebotomy",
        labelFallback: "Lab Phlebotomy",
        icon: "ScienceIcon",
        iconColor: "#4db6ac",
        permissions: [PERMISSIONS.LAB_READ],
      },
      {
        id: "assignments",
        path: "/assignments",
        labelKey: "nav.assignments",
        labelFallback: "Assignments",
        icon: "PersonIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.ASSIGNMENTS_WRITE],
      },
    ],
  },
  {
    id: "care-management",
    labelKey: "nav.careManagement",
    labelFallback: "Care Management",
    icon: "FavoriteBorderIcon",
    permissions: [
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.CAREPLANS_READ,
    ],
    items: [
      {
        id: "discharged",
        path: "/clinical-info/discharged",
        labelKey: "nav.discharged",
        labelFallback: "Discharged",
        icon: "ExitToAppIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "admitted",
        path: "/clinical-info/admitted",
        labelKey: "nav.admitted",
        labelFallback: "Admitted",
        icon: "LocalHospitalIcon",
        iconColor: "#42a5f5",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "emergency",
        path: "/clinical-info/emergency",
        labelKey: "nav.emergency",
        labelFallback: "Emergency",
        icon: "EmergencyIcon",
        iconColor: "#ff7043",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "high-cost",
        path: "/clinical-info/high-cost",
        labelKey: "nav.highCost",
        labelFallback: "High Cost",
        icon: "AttachMoneyIcon",
        iconColor: "#66bb6a",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "high-risk",
        path: "/clinical-info/high-risk",
        labelKey: "nav.highRisk",
        labelFallback: "High Risk",
        icon: "WarningIcon",
        iconColor: "#ffa726",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "care-plans",
        path: "/care-plan",
        labelKey: "nav.carePlans",
        labelFallback: "Care Plans",
        icon: "AssignmentIcon",
        iconColor: "#26a69a",
        permissions: [PERMISSIONS.CAREPLANS_READ],
      },
      {
        id: "patient-profiles",
        path: "/patient-profiles",
        labelKey: "nav.patientProfiles",
        labelFallback: "Patient Profiles",
        icon: "AssignmentIndIcon",
        iconColor: "#8e24aa",
        permissions: [PERMISSIONS.PATIENTS_WRITE],
      },
    ],
  },
  {
    id: "general",
    labelKey: "nav.general",
    labelFallback: "General",
    icon: "CalendarMonthIcon",
    permissions: null, // Accesible para todos los autenticados
    items: [
      {
        id: "schedule",
        path: "/general",
        labelKey: "nav.schedule",
        labelFallback: "Schedule",
        icon: "CalendarMonthIcon",
        iconColor: "#667EEA",
        permissions: null,
      },
      {
        id: "bug-report",
        path: "/bug-report",
        labelKey: "nav.bugReport",
        labelFallback: "Bug Report",
        icon: "BugReportIcon",
        iconColor: "#EF4444",
        permissions: null,
      },
    ],
  },
  {
    id: "administration",
    labelKey: "nav.administration",
    labelFallback: "Administration",
    icon: "AdminPanelSettingsIcon",
    permissions: [PERMISSIONS.ADMIN_USERS, PERMISSIONS.SUPPORT_ACCESS, PERMISSIONS.DOCUMENTS_READ],
    items: [
      {
        id: "administration",
        path: "/administration",
        labelKey: "nav.administration",
        labelFallback: "Administration",
        icon: "AdminPanelSettingsIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.ADMIN_USERS],
      },
      {
        id: "employee-documents",
        path: "/employee-documents",
        labelKey: "nav.employeeDocuments",
        labelFallback: "Employee Documents",
        icon: "DescriptionIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.DOCUMENTS_READ],
      },
      {
        id: "support",
        path: "/support",
        labelKey: "nav.technicalSupport",
        labelFallback: "Technical Support",
        icon: "BugReportIcon",
        iconColor: "#9e9e9e",
        permissions: [PERMISSIONS.SUPPORT_ACCESS],
      },
    ],
  },
];

export const APPBAR_ITEMS = [
  {
    id: "dashboard",
    path: "/main",
    labelKey: "nav.dashboard",
    labelFallback: "Dashboard",
    icon: "HomeIcon",
    permissions: null, // Accesible para todos
  },
  {
    id: "patient-map",
    path: "/patientMap",
    labelKey: "nav.patientMap",
    labelFallback: "Patient Map",
    icon: "MapIcon",
    permissions: [PERMISSIONS.PATIENTS_READ],
  },
];

export default SIDEBAR_SECTIONS;
